import React, { useState } from 'react';
import { Modal, Alert } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useAppDispatch } from 'src/hooks';
import { mfaVerifyLogin } from 'src/reducers/user';
import { setAuthToken } from 'src/utils/auth';
import { Button, Input, Text } from 'src/shared';
import { Size } from 'src/shared/text';
import { Colors } from 'src/utils/color';
import { verboseLog } from 'src/utils/config';

interface MFAVerifyModalProps {
    show: boolean;
    onHide: () => void;
    email: string;
    onSuccess: () => void;
}

const MFAVerifyModal: React.FC<MFAVerifyModalProps> = ({ show, onHide, email, onSuccess }) => {
    const dispatch = useAppDispatch();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [loading, setLoading] = useState(false);

    const validationSchema = yup.object().shape({
        code: yup
            .string()
            .required('MFA code is required')
            .min(6, 'Code must be at least 6 digits')
            .max(8, 'Code must be at most 8 digits')
            .matches(/^\d+$/, 'Code must contain only numbers')
    });

    const handleVerify = async (code: string) => {
        setLoading(true);
        setErrorMessage('');
        
        try {
            const response = await dispatch(mfaVerifyLogin({ email, code })).unwrap();
            if (response?.success) {
                setAuthToken(response?.data?.auth_token);
                verboseLog('✅ MFA verification successful, login complete');
                onSuccess();
            } else {
                setErrorMessage(response?.message || 'Invalid MFA code');
            }
        } catch (err: any) {
            verboseLog('❌ MFA verification error:', err);
            setErrorMessage(err?.data?.message || 'Invalid MFA code. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>MFA Verification Required</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info">
                    Please enter the 6-digit code from your authenticator app, or an 8-digit backup code.
                </Alert>
                
                <Formik
                    initialValues={{ code: '' }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => handleVerify(values.code)}
                >
                    {({
                        values,
                        handleChange,
                        handleSubmit,
                        errors,
                        touched
                    }) => (
                        <Form onSubmit={handleSubmit}>
                            {errorMessage && (
                                <Alert variant="danger" style={{ marginBottom: '15px' }}>
                                    {errorMessage}
                                </Alert>
                            )}
                            
                            <Input
                                label="MFA Code"
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                                    const syntheticEvent = {
                                        ...e,
                                        target: { ...e.target, value, name: 'code' }
                                    };
                                    handleChange('code')(syntheticEvent);
                                }}
                                type="text"
                                name="code"
                                value={values.code}
                                placeholder="000000 or backup code"
                            />
                            {touched.code && errors.code && (
                                <Text
                                    marginLeft={50}
                                    marginBottom={0}
                                    color={Colors.extra.red}
                                >
                                    {errors.code}
                                </Text>
                            )}
                            
                            <Text marginTop={10} fontSize={Size.X5Small} color={Colors.extra.dark_gray}>
                                Enter the 6-digit code from your authenticator app, or an 8-digit backup code if you don't have access to your authenticator.
                            </Text>
                            
                            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <Button 
                                    type="button" 
                                    backgroundColor="#6c757d"
                                    borderColor="#6c757d"
                                    onClick={onHide}
                                >
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={loading}>
                                    {loading ? 'Verifying...' : 'Verify & Login'}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </Modal.Body>
        </Modal>
    );
};

export default MFAVerifyModal;

